import { useCallback, useState } from 'react';
import {
  AlignCenterOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  EditOutlined,
  FormOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Input, Switch, Typography } from 'antd';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { useProcManageActions } from '@/features/proc-management';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { ComponentType } from '@/shared/types';
import { Col } from '@/shared/ui';
import { EditComponentItem } from './EditComponentItem';

const { Title, Text } = Typography;

export const EditProcComponentsTab = () => {
  const { theme } = useThemeContext();
  const {
    selectedStep,
    setSelectedStep,
    proc,
    commentsLabel,
    setCommentsLabel,
    aiAssistantStepContext,
    setAiAssistantStepContext,
  } = useProcContext();
  const { updateStep } = useProcManageActions();

  const [commentsEnabledLoading, setCommentsEnabledLoading] = useState(false);

  const toggleComments = useCallback(
    (checked: boolean) => {
      if (selectedStep?.id) {
        setCommentsEnabledLoading(true);
        updateStep({
          stepId: selectedStep.id,
          data: { commentsEnabled: checked },
          onSuccess: () => setCommentsEnabledLoading(false),
          onError: () => setCommentsEnabledLoading(false),
        });
      }
    },
    [selectedStep?.id, updateStep],
  );

  const handleCommentsLabelChange = useCallback(() => {
    if (selectedStep?.id) {
      updateStep({
        stepId: selectedStep.id,
        data: { commentsLabel },
        onSuccess: () => setSelectedStep({ ...selectedStep, commentsLabel }),
      });
    }
  }, [commentsLabel, selectedStep, setSelectedStep, updateStep]);

  const handleAiAssistantStepContextChange = useCallback(() => {
    if (selectedStep?.id) {
      updateStep({
        stepId: selectedStep.id,
        data: { commentsLabel },
        onSuccess: () =>
          setSelectedStep({ ...selectedStep, aiAssistantStepContext }),
      });
    }
  }, [
    aiAssistantStepContext,
    commentsLabel,
    selectedStep,
    setSelectedStep,
    updateStep,
  ]);

  return (
    <Wrapper>
      <SidebarHeader>
        <Title level={4} style={{ textAlign: 'center', width: '100%' }}>
          Components
        </Title>
      </SidebarHeader>
      <Title
        level={5}
        style={{ textAlign: 'left', width: '100%', paddingLeft: 15 }}
      >
        General
      </Title>
      <Grid data-testid="general-components">
        <EditComponentItem
          id={ComponentType.Text}
          title="Text"
          icon={
            <AlignCenterOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
        <EditComponentItem
          id={ComponentType.Image}
          title="Image"
          icon={
            <PictureOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
        <EditComponentItem
          id={ComponentType.Video}
          title="Video"
          icon={
            <PlayCircleOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
        <EditComponentItem
          id={ComponentType.Embedding}
          title="Embed"
          icon={
            <CodeOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
      </Grid>
      <Title
        level={5}
        style={{
          textAlign: 'left',
          width: '100%',
          paddingLeft: 15,
          marginTop: 15,
        }}
      >
        User Input
      </Title>
      <Grid data-testid="user-input-components">
        <EditComponentItem
          id={ComponentType.Input}
          title="Input"
          icon={
            <EditOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
        <EditComponentItem
          id={ComponentType.TextArea}
          title="TextArea"
          icon={
            <FormOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
        <EditComponentItem
          id={ComponentType.Checkbox}
          title="Checkbox"
          icon={
            <CheckCircleOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
        <EditComponentItem
          id={ComponentType.Select}
          title="Select"
          icon={
            <UnorderedListOutlined
              style={{
                fontSize: 28,
                color: themeConfig[theme].colors.layoutOverlayContentBg,
              }}
            />
          }
        />
      </Grid>
      <StepPropertiesWrapper data-testid="step-properties">
        <Title
          level={4}
          style={{ textAlign: 'center', width: '100%', marginTop: 20 }}
        >
          Step properties
        </Title>
        <Col alignItems="flex-start" style={{ width: '100%', paddingTop: 10 }}>
          <CommentsLabelWrapper>
            <label style={{ color: themeConfig[theme].colors.text }}>
              Live chat label
            </label>
            <Input
              data-testid="comments-label-input"
              value={commentsLabel}
              style={{ marginTop: 10 }}
              onChange={(e) => setCommentsLabel(e.target.value)}
              onBlur={() => handleCommentsLabelChange()}
            />
          </CommentsLabelWrapper>
          <label style={{ color: themeConfig[theme].colors.text }}>
            Agent chat
          </label>
          <div style={{ marginTop: 10 }}>
            <Switch
              data-testid="comments-switch"
              checked={selectedStep?.commentsEnabled}
              loading={commentsEnabledLoading}
              onChange={(checked) => toggleComments(checked)}
              style={{ marginRight: 10 }}
            />
            <Text data-testid="comments-switch-label">
              {selectedStep?.commentsEnabled ? 'enabled' : 'disabled'}
            </Text>
          </div>
        </Col>
        {proc?.stepData.administration.aiAssistantEnabled && (
          <AIPromptLabelWrapper>
            <label style={{ color: themeConfig[theme].colors.text }}>
              AI Assistant Step Context
            </label>
            <Input.TextArea
              data-testid="ai-assistant-step-context-input"
              value={aiAssistantStepContext}
              rows={5}
              maxLength={250}
              showCount={true}
              style={{ marginTop: 10 }}
              onChange={(e) => setAiAssistantStepContext(e.target.value)}
              onBlur={() => handleAiAssistantStepContextChange()}
            />
          </AIPromptLabelWrapper>
        )}
      </StepPropertiesWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 30px;
`;

const Grid = styled.div`
  width: 100%;
  padding: 15px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  grid-gap: 15px;
  align-items: stretch;
  justify-items: center;
`;

const SidebarHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  padding: 15px;
`;

const StepPropertiesWrapper = styled.div`
  padding: 15px;
  width: 100%;
`;

const CommentsLabelWrapper = styled.div`
  width: 100%;
  margin-bottom: 30px;
`;

const AIPromptLabelWrapper = styled.div`
  width: 100%;
  margin-top: 30px;

  .ant-input-data-count {
    font-size: 11px;
  }
`;
